import React, { createContext, Fragment, useContext } from 'react'
import { components } from 'react-select'

import Select from '../../../../components/ReactSelect'
import CheckboxSelect from '../../../../components/CheckboxSelect'
import { DateRangePicker } from '../../../../components/DatePicker'

import * as date from 'Utils/date'

export const TrainingFilterContext = createContext({
  resetPage: undefined,
  dateRange: [],
  setDateRange: undefined,
  feedback: false,
  setFeedback: undefined,
  reviewStatus: [],
  setReviewStatus: undefined
})

const useTrainingFilterContext = () => useContext(TrainingFilterContext)

const DEFAULT_PAGE_SIZE = 10
const DEFAULT_PAGE = 1
const FEEDBACK_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Helpful', value: 'helpful' },
  { label: 'Not Helpful', value: 'not_helpful' },
  { label: 'Not Listed', value: 'not_listed' }
]
const REVIEW_STATUS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Content Required', value: 'content_required' },
  { label: 'Training Required', value: 'training_required' },
  { label: 'Trained', value: 'trained' },
  { label: 'Ignore', value: 'ignore' }
]

const VALID_REVIEW_STATUSES = REVIEW_STATUS.map((st) => st.value)

const isValidFeedback = (value) => {
  return FEEDBACK_FILTER_OPTIONS.some((filter) => filter.value === value)
}

export const parseFiltersFromURL = () => {
  const params = new URLSearchParams(location.search)

  const paramsSize = params.get('size') || DEFAULT_PAGE_SIZE
  const paramsPage = params.get('page') || DEFAULT_PAGE

  const page = isNaN(paramsPage) ? paramsPage : parseInt(paramsPage)
  const size = isNaN(paramsSize) ? DEFAULT_PAGE_SIZE : parseInt(paramsSize)

  const paramsDateRangeStart = params.get('start_date')
  const paramsDateRangeEnd = params.get('end_date')
  const paramsFeedback = params.get('feedback')
  const paramsReviewStatus = params.get('review_status') ?? ''

  const dateRange = []

  if (date.isValid(paramsDateRangeStart)) {
    dateRange[0] = date.format(paramsDateRangeStart, 'MM-DD-YYYY')
  }

  if (date.isValid(paramsDateRangeEnd) && dateRange[0]) {
    dateRange[1] = date.format(paramsDateRangeEnd, 'MM-DD-YYYY')
  }

  return {
    page,
    size,
    dateRange,
    feedback: isValidFeedback(paramsFeedback) ? paramsFeedback : null,
    reviewStatus: paramsReviewStatus.split(',').filter((status) => VALID_REVIEW_STATUSES.includes(status))
  }
}

export const getInitialApiSearchParams = (params) => {
  const initialParams = {
    offset: (params.page - 1) * params.size,
    limit: params.size
  }

  const [startDate, endDate] = params.dateRange
  if (startDate) {
    initialParams.start_date = startDate
  }

  if (endDate) {
    initialParams.end_date = endDate
  }

  if (params.feedback !== null) {
    initialParams.feedback = params.feedback
  }

  if (params.reviewStatus.length !== 0) {
    initialParams.review_status = params.reviewStatus
  }

  return initialParams
}

const DateRangeFilter = () => {
  const { resetPage, dateRange, setDateRange } = useTrainingFilterContext()

  const handleChange = (range = []) => {
    resetPage()
    setDateRange(range || [])
  }

  return <DateRangePicker maxDate={new Date()} value={dateRange} onChange={handleChange} />
}

const ReviewFilter = () => {
  const { resetPage, reviewStatus, setReviewStatus } = useTrainingFilterContext()

  const value = REVIEW_STATUS.filter((status) => reviewStatus.includes(status.value))

  const handleChange = (selectedStatuses) => {
    resetPage()
    const statuses = selectedStatuses.map((status) => status.value)
    setReviewStatus(statuses)
  }

  return (
    <CheckboxSelect
      placeholder='Review status'
      options={REVIEW_STATUS}
      selectedLabelPrefix='Review status ($count)'
      isClearable={false}
      value={value}
      onChange={handleChange}
    />
  )
}

const HelpfulFilter = () => {
  const { resetPage, feedback, setFeedback } = useTrainingFilterContext()

  const handleChange = (e) => {
    resetPage()
    const selectedFeedback = e.value !== 'all' ? e.value : null
    setFeedback(selectedFeedback)
  }

  const selectedFeedback = FEEDBACK_FILTER_OPTIONS.find((opt) => opt.value === feedback)
  const value = selectedFeedback ?? null

  return (
    <Select
      placeholder='Feedback'
      options={FEEDBACK_FILTER_OPTIONS}
      value={value}
      onChange={handleChange}
      components={{
        SingleValue: (props) => {
          return <components.SingleValue {...props}>Feedback: {props.children}</components.SingleValue>
        }
      }}
    />
  )
}

const TrainingFilters = () => {
  return (
    <Fragment>
      <DateRangeFilter />
      <ReviewFilter />
      <HelpfulFilter />
    </Fragment>
  )
}

export default TrainingFilters
